import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Grid, useMediaQuery, useTheme } from '@material-ui/core';
import OrderSelect from './shared/OrderSelect';
import {
  compareUsersByExperience,
  compareUsersByUpdated,
  compareUsersByName,
  compareUsersByNegativeWorkload,
  compareUsersByWorkload,
} from 'helpers/compareFunctions';
import ReactLoading from 'react-loading';
import { makeStyles } from '@material-ui/styles';
import { useGetCurrentUserQuery, usePrefetch } from 'utils/wemble-api';
import { PublicUserDto, UserDto, UserSearchParams } from 'utils/wemble-api.generated';
import searchIcon from 'assets/icons/search.svg';
import { trackAction } from 'utils/trackAction';
import UtilizationPage from './Home/UtilizationPage';
import organization from 'utils/organization';
import DetailedPage from './Home/DetailedPage';
import ViewSelect from './shared/ViewSelect';
import LazyStatusCard from './StatusCard/LazyStatusCard';

const useStyles = makeStyles(() => ({
  filter_grid: {
    paddingBottom: 16,
  },
  loader: {
    fill: '#59abcb',
    margin: 'auto',
    marginTop: 50,
    marginBottom: 50,
    width: '10%',
  },
  searchField: {
    '& > div': {
      backgroundColor: 'white !important',
    },
    '& input': {
      fontSize: '14px !important'
    }
  },
  searchContainer: {
    flexShrink: 0,
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    backgroundColor: 'white',
    borderRadius: 16,
    height: 48,
  },
  searchIcon: {
    position: 'absolute',
    left: 12,
    width: 20,
    height: 20,
    opacity: 0.7,
    pointerEvents: 'none',
  },
  searchInput: {
    fontFamily: 'Roboto',
    fontSize: 14,
    fontWeight: 400,
    padding: '0 12px 0 34px',
    borderRadius: 16,
    border: 'none',
    outline: 'none',
    backgroundColor: 'transparent',
    width: 140,
    height: '100%',
    color: '#777',
    '&::placeholder': {
      color: '#777',
      opacity: 1,
    },
  },
}));

export type StatusCardListOrderBy = 'updated' | 'name' | 'workload-desc' | 'workload-asc' | 'experience';

const orderOptions: { label: string; value: StatusCardListOrderBy }[] = [
  {
    label: 'Last updated',
    value: 'updated',
  },
  {
    label: 'Name',
    value: 'name',
  },
  {
    label: 'Capacity (asc)',
    value: 'workload-asc',
  },
  {
    label: 'Capacity (desc)',
    value: 'workload-desc',
  },
];

const sortUsers = (order: StatusCardListOrderBy) => (userA: UserDto, userB: UserDto) => {
  switch (order) {
    default:
    case 'updated':
      return compareUsersByUpdated(userA, userB);
    case 'name':
      return compareUsersByName(userA, userB);
    case 'workload-desc':
      return compareUsersByNegativeWorkload(userA, userB);
    case 'workload-asc':
      return compareUsersByWorkload(userA, userB);
    case 'experience':
      return compareUsersByExperience(userA, userB);
  }
};

const sortByIsCurrent = (currentUser: UserDto | null | undefined, order: string) => (userA: UserDto, userB: UserDto) => {
  if (order !== 'updated') {
    return 0;
  }
  if (userA._id === currentUser?._id) {
    return -1;
  }
  if (userB._id === currentUser?._id) {
    return 1;
  }
  return 0;
};

const StatusCardList = ({
  onClick,
  users,
  isFetching,
  officeOrGroupSelected,
  filterBar,
  searchParams,
}: {
  onClick: (user: PublicUserDto) => void;
  users: PublicUserDto[] | undefined;
  isFetching: boolean;
  officeOrGroupSelected: boolean;
  filterBar?: React.ReactNode;
  searchParams?: UserSearchParams;
}) => {

  const classes = useStyles();
  const { data: currentUser } = useGetCurrentUserQuery();

  const showDetailed = organization(currentUser?.company).enableUtilizationView && (currentUser?.detailedViewPriviliges || (organization(currentUser?.company).detailedViewAdminDefault && (currentUser?.admin || currentUser?.administratorPriviliges || currentUser?.companyAdmin)))
  const showUtilizationOthers = organization(currentUser?.company).enableUtilizationView && (currentUser?.utilizationViewOthersPriviliges || (organization(currentUser?.company).detailedViewAdminDefault && (currentUser?.admin || currentUser?.administratorPriviliges || currentUser?.companyAdmin)))


  const views = [{
    label: 'Capacity',
    value: 'overview',
  },
  ...(showDetailed ? [{
    label: 'Matters',
    value: 'detailed',
  }] : []),
  ...(showUtilizationOthers ? [
    {
      label: 'Utilization',
      value: 'utilization',
    }] : (showDetailed ? [{
      label: 'My Utilization',
      value: 'my-utilization',
    }] : []))
  ]





  //   ((organization(currentUser?.company).enableUtilizationView && ((organization(currentUser?.company).name == "Synch" && (currentUser?.admin || currentUser?.administratorPriviliges || currentUser?.companyAdmin)) || ["66309b91113dc300150542f6", "5e425ccb6d4ea9001678e002", "5daeb8637e1c4600165d587f", "5da2f6c75438640016f8da9f"].includes(typeof currentUser?.group == 'string' ? currentUser?.group : (currentUser?.group?._id || '')) || ["Maria Antrea", "Mike Specter"].includes(currentUser?.name || ''))) ? (currentUser?.admin || currentUser?.administratorPriviliges || currentUser?.companyAdmin) ? [{
  //   label: 'Overview',
  //   value: 'overview',
  // },
  // {
  //   label: 'Detailed',
  //   value: 'detailed',
  // },
  // {
  //   label: 'Utilization',
  //   value: 'utilization',
  // }] : [{
  //   label: 'Overview',
  //   value: 'overview',
  // }, {
  //   label: 'Detailed',
  //   value: 'detailed',
  // },
  // {
  //   label: 'My Utilization',
  //   value: 'utilization',
  // }] : [{
  //   label: 'Overview',
  //   value: 'overview',
  // }]);

  const org = organization(currentUser?.company);
  const prefetchBulkWeeklyHours = usePrefetch('bulkWeeklyHours' as any);
  const prefetchBulkRecentMatters = usePrefetch('bulkRecentMatters' as any);

  const userIds = users?.map(u => u._id).filter(Boolean) as string[] || [];
  const handleViewHover = (option: any) => {
    if (!userIds.length) return;
    if (option.value === 'utilization') {
      prefetchBulkWeeklyHours({ userIds, weeks: 4 }, { ifOlderThan: 60 });
    } else if (option.value === 'my-utilization' && currentUser?._id) {
      prefetchBulkWeeklyHours({ userIds: [currentUser._id], weeks: 4 }, { ifOlderThan: 60 });
    } else if (option.value === 'detailed') {
      prefetchBulkRecentMatters({ userIds, days: 14 }, { ifOlderThan: 60 });
    }
  };

  const [order, setOrder] = useState((views.length == 1 && orderOptions.find(o => o.value == localStorage.getItem("OverviewPageSortMethod"))?.value) || orderOptions[0].value);
  const [searchTerm, setSearchTerm] = useState('');
  // A group link on a person card (/home?group=…) shows the whole group: clear the search box too.
  const { search: locationSearch } = useLocation();
  useEffect(() => {
    if (new URLSearchParams(locationSearch).has('group')) setSearchTerm('');
  }, [locationSearch]);
  const [detailedLayout, setLayout] = useState<string>(localStorage.getItem("DetailedPageLayout") || 'timekeeper');

  const theme = useTheme();
  const xs = useMediaQuery(theme.breakpoints.down('xs'))
  const sm = useMediaQuery(theme.breakpoints.down('sm'))
  const statusCardSpacing = xs ? 2 : (sm ? 2 : 3);

  // Crowell (and any other firm that opts in) gets the search input + sort dropdown
  // on mobile too, dropped onto a second row below the filter bar.
  const mobileSearchSort = sm && org.mobileSearchAndSort;
  const showSearchInput = !sm || mobileSearchSort;
  const showSortSelect = !sm || mobileSearchSort;

  const [view, setView] = useState(views[0]);

  const shownUsers = [...(users ?? [])]
    .filter(u => !searchTerm ||
      u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.title?.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort(searchParams?.experience?.length ? () => 0 : sortUsers(order))
    .sort(searchParams?.experience?.length ? () => 0 : sortByIsCurrent(currentUser, order));

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
        <div style={{
          flex: 1,
          minWidth: 0,
          backgroundColor: 'white',
          borderRadius: 16,
          padding: '1px 6px',
          minHeight: 48,
          display: 'flex',
          alignItems: 'center',
          // On mobile with search/sort enabled, force the filter bar onto its own row so
          // the search input + sort wrap to a second row underneath.
          ...(mobileSearchSort ? { flexBasis: '100%' } : {}),
        }}>
          {filterBar}
        </div>
        {showSearchInput && <div
          className={classes.searchContainer}
          style={mobileSearchSort ? { flex: 1, height: 40 } : undefined}
        >
          <img
            src={searchIcon}
            alt=""
            className={classes.searchIcon}
          />
          <input
            type="text"
            placeholder="Search"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className={classes.searchInput}
            style={mobileSearchSort ? { width: '100%' } : undefined}
          />
        </div>}
        <div style={{ flexShrink: 0, ...(sm && !mobileSearchSort ? { width: '100%' } : {}) }}>
          {views.length == 1 ? (showSortSelect && <OrderSelect
            options={orderOptions}
            onChange={(order) => {
              setOrder(order.value);
              localStorage.setItem("OverviewPageSortMethod", order.value)
            }}
            defaultValue={orderOptions.find(o => o.value == localStorage.getItem("OverviewPageSortMethod")) || orderOptions[0]}
            compact={mobileSearchSort}
          />) : <ViewSelect
            options={views}
            onChange={(view) => {
              setView(view);
              trackAction('switchView', { view: view.value });
            }}
            defaultValue={views[0]}
            onHover={handleViewHover}
          />}
        </div>
      </div>


      {view.value == "utilization" && (
        <UtilizationPage filteredUsers={users} searchTerm={searchTerm} />)}

      {view.value == "my-utilization" && currentUser && (
        <UtilizationPage filteredUsers={[currentUser as any as PublicUserDto]} />)}

      {view.value == "detailed" && (
        <DetailedPage filteredUsers={users} setLayout={setLayout} searchTerm={searchTerm} />)}


      {view.value == "overview" && (
        (isFetching && !users?.length) ? (
          <ReactLoading type={'spinningBubbles'} className={classes.loader} />
        ) : (
          officeOrGroupSelected && (shownUsers.length === 0 ? <NoResults searchTerm={searchTerm} /> :
            <Grid container spacing={statusCardSpacing} >
              {shownUsers
                .map((employee) => (
                  <LazyStatusCard key={`home-user-${employee._id}`}
                    user={employee}
                    orderOption={order}
                    handleClick={() => {
                      onClick(employee);
                    }}
                    searchParams={searchParams} />
                ))}
            </Grid>
          )
        ))}
    </>
  );
};

// Shown when the search or filters leave nobody
const NoResults = ({ searchTerm }: { searchTerm: string }) => (
  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', padding: '56px 16px 64px' }}>
    <svg width="168" height="120" viewBox="0 0 168 120" fill="none" aria-hidden="true">
      <rect x="14" y="22" width="70" height="84" rx="14" fill="#F1F5F8" />
      <circle cx="49" cy="50" r="13" fill="#E2E9EE" />
      <rect x="31" y="72" width="36" height="6" rx="3" fill="#E2E9EE" />
      <rect x="38" y="84" width="22" height="5" rx="2.5" fill="#E9EFF3" />
      <rect x="62" y="10" width="70" height="84" rx="14" fill="white" stroke="#E2E9EE" strokeWidth="2" />
      <circle cx="97" cy="38" r="13" fill="#EDF2F5" />
      <rect x="79" y="60" width="36" height="6" rx="3" fill="#EDF2F5" />
      <rect x="86" y="72" width="22" height="5" rx="2.5" fill="#F1F5F8" />
      <circle cx="122" cy="76" r="20" fill="white" stroke="#59abcb" strokeWidth="5" />
      <path d="M136.5 90.5L152 106" stroke="#59abcb" strokeWidth="7" strokeLinecap="round" />
      <path d="M113 70a11 11 0 0 1 9-5" stroke="#9FD0E3" strokeWidth="3" strokeLinecap="round" />
    </svg>
    <div style={{ marginTop: 20, fontSize: 18, fontWeight: 600, color: '#2d2d2d' }}>
      {searchTerm ? `No matches for “${searchTerm}”` : 'No one matches these filters'}
    </div>
    <div style={{ marginTop: 6, fontSize: 14, color: '#7a8189', maxWidth: 360 }}>
      {searchTerm ? 'Try another name or title, or clear the search.' : 'Try removing a filter to see more people.'}
    </div>
  </div>
);

export default StatusCardList;
